import React, { FC, useEffect, useRef, useState } from 'react';
import { EnrichedOrder, OrdersGrid } from '~Scenes/EventedKitchenDisplay/view';
import Icon from '~ComponentsImproved/Icon';
import UpcomingEventIcon from '~Assets/icons/upcoming_event.svg';
import Modal, { insideWrapperStyles } from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Box } from '~Styles';
import { useStorage } from '~Components/Storage';
import Masonry from 'react-masonry-css';
import Order from '../Order';
import { ItemWithCourseType } from '~Scenes/EventedKitchenDisplay';
import Button from '~ComponentsImproved/Button';
import useI18n from '~Translations';
import { groupOrdersByDate } from '~Scenes/EventedKitchenDisplay/utils';
import DateSeparator from '../DateSeparator';

interface Props {
  ordersDayOrMoreAway: EnrichedOrder[];
  venueId: string;
  itemsWithCourseType: ItemWithCourseType[];
}

const Wrapper = styled(Box)`
  height: 90%;
  width: 80vw;
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  overflow: hidden;
`;

const HeaderBar = styled(Box)`
  height: 48px;
  border-bottom: 1px solid var(--colorBlueGray1000);
  flex-shrink: 0;
`;

const ContentArea = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px;
`;

const Circle = styled(Box)<{ hasUnseenOrders: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ hasUnseenOrders }) =>
    hasUnseenOrders ? 'var(--colorUIRed)' : 'var(--colorBlueGray1100)'};
  color: var(--colorWhite);
  font-weight: bold;
  font-size: 1rem;
  text-align: center;
`;

/*
 * This component displays a button that opens a modal showing pre-orders due tomorrow or later.
 * It also tracks unseen orders by monitoring the changes in ordersDayOrMoreAway length.
 */
const OrdersDayOrMoreAway: FC<Props> = ({ ordersDayOrMoreAway, venueId, itemsWithCourseType }) => {
  const { i18n } = useI18n();
  const futureOrdersModal = useModal();
  const [{ showMasonryGrid, showFiveColumns }] = useStorage();

  const [unseenOrders, setUnseenOrders] = useState<number>(0);

  /*
   * Use a ref to keep track of the previous length of ordersDayOrMoreAway.
   * This allows comparison to determine if the length has decreased.
   */
  const previousOrdersLengthRef = useRef<number>(ordersDayOrMoreAway.length);

  /*
   * useEffect to react to changes in ordersDayOrMoreAway length.
   * - Update "upcomingPreorders" in localStorage when the length decreases.
   * - Set unseenOrders when there are new orders that haven't been viewed.
   */
  useEffect(() => {
    const previousLength = previousOrdersLengthRef.current;
    const storedUpcomingPreorders = parseInt(localStorage.getItem('upcomingPreorders') || '0', 10);

    // Check if the ordersDayOrMoreAway length is greater than the stored count in localStorage
    if (ordersDayOrMoreAway.length > storedUpcomingPreorders) {
      setUnseenOrders(ordersDayOrMoreAway.length - storedUpcomingPreorders);
    }

    // If the length decreases, update localStorage
    if (ordersDayOrMoreAway.length < previousLength) {
      localStorage.setItem('upcomingPreorders', `${ordersDayOrMoreAway.length}`);
      setUnseenOrders(0); // Reset unseenOrders as the user has effectively "seen" them
    }

    previousOrdersLengthRef.current = ordersDayOrMoreAway.length;
  }, [ordersDayOrMoreAway.length]);

  /*
   * Update unseenOrders and reset localStorage when the modal is opened.
   */
  const handleModalToggle = () => {
    setUnseenOrders(0);
    localStorage.setItem('upcomingPreorders', `${ordersDayOrMoreAway.length}`);
    futureOrdersModal.toggle();
  };

  const masonryBreakPoints = showFiveColumns
    ? { default: 5, 1500: 5, 1200: 4, 750: 3, 350: 2 }
    : { default: 4, 1500: 4, 1200: 3, 750: 2, 350: 1 };

  const renderOrders = (ordersToRender: EnrichedOrder[]) => {
    return ordersToRender.map((enrichedOrder) => (
      <Order
        key={enrichedOrder.order.id}
        order={enrichedOrder.order}
        realOrder={enrichedOrder.realOrder}
        venueId={venueId}
        items={enrichedOrder.order.items}
        itemsWithCourseType={itemsWithCourseType}
      />
    ));
  };

  // Group future orders by date
  const groupedOrdersByDate = groupOrdersByDate(ordersDayOrMoreAway);

  return (
    <>
      {ordersDayOrMoreAway.length > 0 && (
        <div>
          <Button height="42px" styleType="Gray" onTouchEnd={handleModalToggle}>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Circle
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="small"
                marginRight="xs"
                hasUnseenOrders={unseenOrders > 0}>
                {ordersDayOrMoreAway.length}
              </Circle>
              {i18n('KDS.OrdersDueTomorrowOnward')}
            </Box>
          </Button>
        </div>
      )}

      <Modal isShowing={futureOrdersModal.isShowing} hide={futureOrdersModal.toggle}>
        <Wrapper display="flex" flexDirection="column" padding="medium">
          <HeaderBar
            display="flex"
            gap="xs"
            alignItems="center"
            justifyContent="center"
            marginBottom="small">
            <Icon
              width="32px"
              height="32px"
              src={UpcomingEventIcon}
              color={'var(--colorBlueGray400)'}
            />
            <Heading color="var(--colorBlueGray400)" level={2}>
              {i18n('KDS.OrdersDueTomorrowOnwardExtended')}
            </Heading>
          </HeaderBar>
          <ContentArea>
            {groupedOrdersByDate.map((group) => (
              <React.Fragment key={group.date}>
                <DateSeparator date={group.date} />
                {showMasonryGrid ? (
                  <Masonry
                    breakpointCols={masonryBreakPoints}
                    className="kds-grid"
                    columnClassName="kds-grid_column">
                    {renderOrders(group.orders)}
                  </Masonry>
                ) : (
                  <OrdersGrid isModal showFiveColumns={showFiveColumns}>
                    {renderOrders(group.orders)}
                  </OrdersGrid>
                )}
              </React.Fragment>
            ))}
          </ContentArea>
        </Wrapper>
      </Modal>
    </>
  );
};

export default OrdersDayOrMoreAway;
